<template>
  <TirPmDropdown
    :variant="TirPmDropdownVariantEnum.Detached"
    class="task-type-dropdown"
    label="Тип"
    placeholder="Выберите тип"
    :options="dropdownOptions"
    :selected-option="selectedOption"
    :size="TirPmDropdownSizeEnum.Light"
    width="fit-content"
    :is-disabled-popover="(options.length <= 1 && !!modelValue) || !editing"
    :isDisabled="!editing"
    popover-width="auto"
  >
    <template #selectedContent>
      <div class="task-type-dropdown__selected-content" v-if="selectedOption">
        {{ selectedOption.Name }}
      </div>
    </template>
    <template #optionList="{ option }">
      <div class="task-type-dropdown__option" @click="emit('update:modelValue', option.Id)">
        <div class="task-type-dropdown__type">
          {{ option.Name }}
        </div>
        <TirPmTooltipElement v-if="option.Description" width="15rem">
          <template #content>
            <div class="task-type-dropdown__option__description">
              {{ option.Description }}
            </div>
          </template>
          <template #default>
            <InformationCircleIcon class="task-type-dropdown__option__icon" />
          </template>
        </TirPmTooltipElement>
      </div>
    </template>
  </TirPmDropdown>
</template>

<script lang="ts" setup>
import { computed, PropType } from 'vue';
import {
  TirPmDropdownSizeEnum,
  TirPmPopover,
  PopoverPositionEnum,
  TirPmTooltipElement,
  TirPmDropdown,
} from 'tir-components';
import { TypeModel } from '../../models';
import { TypeDropdownOptionModel } from './models';
import { InformationCircleIcon } from 'tir-style-system/icons/outline';
import { TirPmDropdownVariantEnum } from 'tir-pm-dropdown/src/enums';

const emit = defineEmits<{
  /**Событие обновления выбранного типа */
  (e: 'update:modelValue', value: string): void;
}>();

const props = defineProps({
  /**Возможность редактировать */
  editing: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  /**Выбранный тип */
  modelValue: {
    type: String,
  },
  /**Типы для выбора */
  options: {
    type: Array as PropType<Array<TypeModel>>,
    default: [],
  },
});

/**Опции дропдауна */
const dropdownOptions = computed(() =>
  props.options.map(
    (option) =>
      new TypeDropdownOptionModel({
        Id: option.Id,
        Name: option.Name,
        Color: option.Color,
        Description: option.Description,
      }),
  ),
);

/**Выбранная опция */
const selectedOption = computed(() =>
  props.modelValue !== undefined
    ? dropdownOptions.value.find((option) => props.modelValue === option.Id)
    : undefined,
);
</script>

<style lang="scss" scoped>
.task-type-dropdown {
  &__option {
    padding: 0.75rem;
    min-width: 7.75rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    &__icon {
      width: 1.5rem;
      height: 1.5rem;
    }
    &__description {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 2.25rem;
      padding: 0 0.5rem;
      word-break: break-word;
    }
  }
  &__type {
    font-size: 1rem;
    line-height: 1.5rem;
    font-weight: 400;
    color: var(--text-primary, #272d37);
    background-color: var(--neutral-translucent-10, #272d371a);
    padding: 0.125rem 0.5rem;
    border-radius: 0.25rem;
  }
  &__selected-content {
    font-size: 0.875rem;
    line-height: 1.25rem;
    font-weight: 500;
    color: var(--neutral-80, #52575f);
    background-color: var(--neutral-translucent-10, #272d371a);
    padding: 0.125rem 0.5rem;
    border-radius: 0.25rem;
  }
}
</style>
